import { createRoute, redirect } from "@tanstack/react-router";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { authClient } from "@/lib/auth";
import { promiseSleep } from "@/lib/utils";
import { appRoute } from "./appRoute";
import { loginRoute } from "./loginRoute";
import { rootRoute } from "./rootRoute";

export const acceptInvitationRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "/accept-invitation/$id",
    beforeLoad: async ({ params, location }) => {
        const { id } = params;
        const session = await authClient.getSession();
        if (!session.data?.user) {
            const { email } = location.search as any;
            throw redirect({
                to: loginRoute.to,
                search: {
                    redirect: window.location.href,
                    email: email,
                    invitation: id,
                },
            });
        } else {
            const { error } = await authClient.organization.acceptInvitation({
                invitationId: id, // required
            });

            if (error) {
                return {
                    hasError: true,
                    code: error.code,
                    message: error.message,
                };
            }
        }
    },
    component: () => {
        const { hasError, message } = acceptInvitationRoute.useRouteContext() as { hasError: boolean; code: string; message: string };

        useEffect(() => {
            if (hasError) {
                return;
            }
            // do a hard refresh nav here. But wait 1 second to show message
            promiseSleep(1000).then(() => {
                window.location.href = appRoute.to;
            });
        }, []);

        if (hasError) {
            return (
                <div className="flex items-center justify-center min-h-screen p-4">
                    <Card className="w-full max-w-md">
                        <CardHeader className="text-center">
                            <CardTitle>Error while accepting invitation</CardTitle>
                            <CardDescription>{message}</CardDescription>
                        </CardHeader>
                        <CardContent className="flex justify-center">
                            <Button nativeButton={false} variant="outline" render={<a href={appRoute.to}>Back to Home</a>}></Button>
                        </CardContent>
                    </Card>
                </div>
            );
        }

        return (
            <div className="flex items-center justify-center min-h-screen p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <CardTitle>Processing Invitation</CardTitle>
                        <CardDescription>Please wait while we accept your invitation...</CardDescription>
                    </CardHeader>
                    <CardContent className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </CardContent>
                </Card>
            </div>
        );
    },
});
